import { Megaphone, MessageSquare } from 'lucide-react';
import { useApi } from '@/lib/useApi';
import { dateTime, humanise, relative } from '@/lib/format';
import {
  Badge, Card, CardHeader, EmptyState, LoadingBlock, PageHeader, Tabs,
} from '@/components/ui';
import { usePortal } from './PortalShell';
import { useState } from 'react';
import type { Message } from '@shared/types';

export default function PortalUpdates() {
  const { data } = usePortal();
  const [tab, setTab] = useState('notices');

  const { data: messages, loading } = useApi<Message[]>('/portal/messages');

  return (
    <div className="space-y-6">
      <PageHeader title="Updates" subtitle="Notices and messages from your landlord" />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: 'notices', label: 'Notices', count: data.announcements.length },
          { id: 'messages', label: 'Messages', count: messages?.length ?? 0 },
        ]}
      />

      {tab === 'notices' && (
        <Card>
          <div className="p-5 sm:p-6">
            {data.announcements.length === 0 ? (
              <EmptyState
                icon={<Megaphone className="h-6 w-6" />}
                title="Nothing posted yet"
                message="Notices about your building — water shut-offs, maintenance, payment instructions — appear here."
              />
            ) : (
              <div className="space-y-4">
                {data.announcements.map((a) => (
                  <div
                    key={a.id}
                    className={`p-4 rounded-xl border ${
                      a.is_pinned ? 'border-brand/30 bg-brand/6' : 'border-line bg-raised/40'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="font-semibold">{a.title}</p>
                      {a.is_pinned && <Badge tone="brand">Pinned</Badge>}
                    </div>
                    <p className="text-sm text-muted mt-2 whitespace-pre-wrap leading-relaxed">{a.body}</p>
                    <p className="text-xs text-faint mt-3">
                      {a.property_name ? `${a.property_name} · ` : ''}{relative(a.published_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {tab === 'messages' && (
        <Card>
          <CardHeader title="Text messages" subtitle="Everything your landlord has sent you" />
          <div className="p-5 sm:p-6 pt-4">
            {loading ? (
              <LoadingBlock rows={4} />
            ) : !messages || messages.length === 0 ? (
              <EmptyState
                icon={<MessageSquare className="h-6 w-6" />}
                title="No messages yet"
                message="Reminders and receipts sent to your phone are also listed here."
              />
            ) : (
              <div className="space-y-3">
                {messages.map((m) => (
                  <div key={m.id} className="p-4 rounded-xl border border-line bg-raised/40">
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <Badge tone="neutral">{humanise(m.purpose)}</Badge>
                      <span className="text-xs text-faint">{dateTime(m.sent_at ?? m.created_at)}</span>
                    </div>
                    <p className="text-sm text-muted whitespace-pre-wrap leading-relaxed">{m.body}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
